import React from 'react';
import { IoMdDownload, IoMdRefresh } from 'react-icons/io';
import { useThemeStore } from '../../../store/themeStore';
import { useAnimeThemeImages } from '../../../hooks/useAnimeThemeImages';
import ListItem from '../../common/ListItem';

const EMOJI_BY_THEME: Record<string, string> = {
    'anime-dragonball-z': '🐲',
    'anime-buu': '🍭',
    'anime-conan': '🔍',
    'anime-beyblade-x': '🌀',
    'anime-tsubasa': '⚽',
    'anime-kuroko': '🏀',
    'anime-slam-dunk': '🏀',
    'anime-pokemon': '⚡',
    'anime-naruto': '🍥',
    'anime-onepiece': '🏴‍☠️',
    'anime-demonslayer': '⚔️',
    'anime-mha': '💥',
    'anime-doraemon': '🤖',
    'anime-shinchan': '👦',
    'anime-jujutsu': '👁️',
    'anime-spyfamily': '🕵️',
    'anime-haikyuu': '🏐',
    'anime-cars': '🏎️',
    'anime-marvel-spiderman': '🕷️',
};

const AnimeThemeSelector: React.FC = () => {
    const { selectedTheme, themes, setTheme } = useThemeStore();
    const animeThemes = themes.filter((t) => t.id.startsWith('anime-'));
    const img = useAnimeThemeImages();

    if (animeThemes.length === 0) return null;

    return (
        <ListItem
            icon={<span className="text-2xl">🎨</span>}
            content={
                <div className="flex w-full flex-col gap-4">
                    <div className="flex items-center gap-2">
                        <span className="text-lg font-bold">Pick Your Hero!</span>
                        <span className="text-2xl">🦸</span>
                    </div>
                    <p className="text-sm opacity-70">Tap a character to play with them.</p>

                    <div className="grid grid-cols-3 gap-3 pr-2 sm:grid-cols-4">
                        {animeThemes.map((theme) => {
                            const isSelected = theme.id === selectedTheme.id;
                            const isLoading = img.loadingId === theme.id;
                            const emoji = EMOJI_BY_THEME[theme.id] || '⭐';

                            return (
                                <button
                                    key={theme.id}
                                    type="button"
                                    onClick={() => setTheme(theme.id)}
                                    className={`relative flex flex-col items-center gap-1 rounded-2xl p-2 transition-all ${
                                        isSelected ? 'scale-105 ring-4' : 'hover:scale-105'
                                    }`}
                                    style={{
                                        backgroundColor: isSelected ? `${theme.color.point}22` : `${theme.color.main}33`,
                                        boxShadow: isSelected ? `0 0 0 3px ${theme.color.point}` : undefined,
                                    }}
                                >
                                    {/* Character image or emoji placeholder */}
                                    <div
                                        className="relative flex size-14 items-center justify-center overflow-hidden rounded-2xl border-2"
                                        style={{ borderColor: theme.color.point }}
                                    >
                                        {theme.bgImage ? (
                                            <img
                                                src={theme.bgImage}
                                                alt={theme.title}
                                                className="size-full object-cover"
                                            />
                                        ) : (
                                            <span className="text-3xl">{emoji}</span>
                                        )}
                                        {isLoading && (
                                            <div className="absolute inset-0 flex items-center justify-center bg-black/40">
                                                <IoMdRefresh size={20} className="animate-spin text-white" />
                                            </div>
                                        )}
                                    </div>
                                    <span className="line-clamp-1 max-w-full text-center text-xs font-bold">
                                        {theme.title}
                                    </span>

                                    {/* Mini action buttons (only show on selected) */}
                                    {isSelected && (
                                        <div className="absolute -right-1 -top-1 flex gap-0.5">
                                            <button
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    img.fetchImage(theme);
                                                }}
                                                disabled={isLoading}
                                                title="Get new picture"
                                                className="flex size-5 items-center justify-center rounded-full border border-white bg-black/60 text-white"
                                            >
                                                <IoMdDownload size={11} />
                                            </button>
                                            {theme.bgImage && (
                                                <button
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        img.nextCandidate(theme);
                                                    }}
                                                    title="Next picture"
                                                    className="flex size-5 items-center justify-center rounded-full border border-white bg-black/60 text-white"
                                                >
                                                    <IoMdRefresh size={11} />
                                                </button>
                                            )}
                                        </div>
                                    )}
                                </button>
                            );
                        })}
                    </div>

                    {img.error && <p className="text-sm text-red-500">⚠️ {img.error}</p>}
                </div>
            }
        />
    );
};

export default AnimeThemeSelector;
